import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Tag } from "@/components/Layout";
import { projectStatusTone } from "@/lib/statusTone";
import GanttChart from "@/components/GanttChart";
import ProjectWizard from "@/components/ProjectWizard";
import ProjectInsights from "@/components/ProjectInsights";
import UnassignedClientRequests from "@/components/UnassignedClientRequests";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import useBulkSelection from "@/hooks/useBulkSelection";
import usePermissions from "@/lib/usePermissions";
import { Plus, Sparkles, Pencil, Trash2, ImagePlus, Loader2, X, LayoutGrid, BarChart3, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { PROJECT_CATEGORIES, PROJECT_CATEGORY_LABEL } from "@/lib/projectCategory";
import TaskRegistryTab from "@/components/TaskRegistryTab";

function projectCountdown(p, msList) {
  const end = p?.end_date ? new Date(p.end_date) : null;
  const ms = msList || [];
  const done = ms.filter((m) => m.status === "done").length;
  const total = ms.length;
  if (!end || isNaN(end)) {
    return total > 0 ? { text: `${done}/${total} done`, tone: "text-slate-400" } : null;
  }
  const delta = end.getTime() - Date.now();
  if (delta <= 0) return { text: `Past due · ${done}/${total} done`, tone: "text-red-600" };
  const weeks = Math.ceil(delta / (1000 * 60 * 60 * 24 * 7));
  const tone = weeks <= 2 ? "text-[#F54800]" : weeks <= 6 ? "text-amber-600" : "text-slate-500";
  return { text: `${weeks}w left · ${done}/${total} done`, tone };
}

function ScopeListEditor({ label, placeholder, items, onChange, testId, max }) {
  const [draft, setDraft] = React.useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (max && items.length >= max) { toast.error(`Max ${max} entries`); return; }
    onChange([...(items || []), v]);
    setDraft("");
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div data-testid={`scope-editor-${testId}`}>
      <Label className="label-overline">{label}{max ? ` (up to ${max})` : ""}</Label>
      <div className="flex items-center gap-2 mt-1">
        <Input
          className="rounded-sm flex-1"
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          data-testid={`scope-input-${testId}`}
        />
        <Button type="button" variant="outline" className="rounded-sm h-9" onClick={add} data-testid={`scope-add-${testId}`}><Plus size={14} /></Button>
      </div>
      {items?.length > 0 && (
        <ul className="mt-2 space-y-1">
          {items.map((it, i) => (
            <li key={`${i}:${it}`} className="flex items-center justify-between gap-2 text-sm bg-slate-50 px-2 py-1.5 rounded-sm border border-slate-100" data-testid={`scope-item-${testId}-${i}`}>
              <span className="break-words"><span className="text-slate-400 mr-1">{i + 1}.</span>{it}</span>
              <button type="button" onClick={() => remove(i)} className="text-slate-400 hover:text-red-600 shrink-0" data-testid={`scope-remove-${testId}-${i}`}><X size={14} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const BU_OPTIONS = ["Strategy", "BU1", "BU2", "BU3", "Operations"];
const STATUS_OPTIONS = [
  { v: "active", label: "Active" },
  { v: "on_hold", label: "On hold" },
  { v: "delayed", label: "Delayed" },
  { v: "completed", label: "Completed" },
  { v: "out_of_scope", label: "Out of scope" },
];
const SERVICE_OPTIONS = [
  { v: "concept_design", label: "Concept Design" },
  { v: "mvp", label: "MVP" },
  { v: "dfm", label: "DFM" },
  { v: "launch", label: "Launch" },
  { v: "other", label: "Other (specify)" },
];

const emptyForm = (defaultBU = "BU1") => ({
  name: "", business_unit: defaultBU, category: "client_delivery", client_name: "", client_user_id: "",
  description: "", status: "active", start_date: "", end_date: "", budget: 0, cover_image: "",
  goals: [], problems: [], outcomes: [],
  service: "concept_design", service_other: "",
  template: "standard",
  daily_task_sync: false,  // Phase 77
  celebration_media_url: "",  // Phase 154
  default_source_id: "",  // Feb 2026 — "Cash Fix": Default Bank Account for this project's revenue
});

// Feb 2026 · Task #2 — persist the BU + Status filters per user so refreshing
// the page (or coming back later) doesn't dump you back on "ALL". localStorage
// stays scoped to the origin so this survives reloads but is auto-cleared
// when a user logs out (see AuthContext logout). Keys namespaced under
// `projects.filters.*` to avoid collisions.
const PROJECTS_BU_KEY = "projects.filters.bu";
const PROJECTS_STATUS_KEY = "projects.filters.status";

export default function Projects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [milestonesByProject, setMilestonesByProject] = useState({});
  const [filter, setFilter] = useState(() => {
    try { return localStorage.getItem(PROJECTS_BU_KEY) || "ALL"; }
    catch { return "ALL"; }
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // project being edited
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [financeSources, setFinanceSources] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [aiLoading, setAiLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const can = usePermissions();
  const canCreate = can("projects_create");
  const canEditProj = can("projects_edit");
  const canDeleteProj = can("projects_delete");
  const isFounder = user?.role === "founder";

  const bulk = useBulkSelection({
    collection: "projects",
    ids: (projects || []).map((p) => p.id),
    onDone: () => load(),
  });

  const load = async () => {
    const { data } = await api.get("/projects");
    setProjects(data);
    const results = await Promise.all(
      data.map((p) => api.get(`/projects/${p.id}/milestones`).then((r) => [p.id, r.data]).catch(() => [p.id, []]))
    );
    setMilestonesByProject(Object.fromEntries(results));
  };

  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    // Phase 98 — strict Client dropdown source is the same Registry-backed
    // endpoint used by ProjectWizard/Create-invoice, so archived Registry
    // clients are excluded here too (was leaking in via GET /users).
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
    // Feb 2026 — "Cash Fix": Default Bank Account dropdown, open to every
    // internal employee (read-only display names, no account numbers).
    api.get("/finance/sources").then((r) => setFinanceSources(r.data)).catch(() => {});
  }, []);

  // Phase 21 — react to ?new=1 from global "+ Create"
  const [params, setParams] = useSearchParams();
  const [wizardOpen, setWizardOpen] = useState(false);

  // Feb 2026 — Global Fixes P2 · replace endless-scroll on the Projects grid
  // with an explicit pagination model. 24 per "page" (3 cols × 8 rows on
  // desktop). Reset whenever the filter or search-params change.
  const PAGE = 24;
  const [visible, setVisible] = useState(PAGE);
  useEffect(() => { setVisible(PAGE); }, [filter, params]);

  useEffect(() => {
    if (params.get("new") === "1") {
      setWizardOpen(true);
      params.delete("new"); setParams(params, { replace: true });
    }
  }, [params, setParams]);

  // Feb 2026 · Task #2 — persist BU filter on change so revisits land where
  // you left off. localStorage cleared on logout via AuthContext.
  useEffect(() => {
    try { localStorage.setItem(PROJECTS_BU_KEY, filter); } catch { /* noop */ }
  }, [filter]);

  // Feb 2026 · Task #2 — restore last status filter on mount if the URL
  // doesn't already carry one (deep-links from dashboard tiles still win).
  useEffect(() => {
    if (params.get("status")) return;
    let saved = null;
    try { saved = localStorage.getItem(PROJECTS_STATUS_KEY); } catch { /* noop */ }
    if (saved) {
      const np = new URLSearchParams(params);
      np.set("status", saved);
      setParams(np, { replace: true });
    }
  }, []);

  // Feb 2026 · Task #2 — mirror the current URL status into localStorage so
  // clicking a chip persists across reloads.
  useEffect(() => {
    const s = params.get("status");
    try {
      if (s) localStorage.setItem(PROJECTS_STATUS_KEY, s);
      else localStorage.removeItem(PROJECTS_STATUS_KEY);
    } catch { /* noop */ }
  }, [params]);

  const startCreate = () => {
    // Phase 21 — launch the multi-step wizard instead of the single-page dialog
    setWizardOpen(true);
  };

  const startEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "", business_unit: p.business_unit || "BU1", category: p.category || "client_delivery",
      client_name: p.client_name || "", client_user_id: p.client_user_id || "",
      description: p.description || "", status: p.status || "active",
      start_date: p.start_date || "", end_date: p.end_date || "",
      budget: p.budget || 0, cover_image: p.cover_image || "",
      goals: p.goals || [], problems: p.problems || [], outcomes: p.outcomes || [],
      service: p.service || "concept_design", service_other: p.service_other || "",
      template: p.template || "standard",
      daily_task_sync: !!p.daily_task_sync,
      celebration_media_url: p.celebration_media_url || "",  // Phase 154
      default_source_id: p.default_source_id || "",  // Feb 2026 — "Cash Fix"
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.client_user_id) { toast.error("Pick a client from the Registry — go to Registry to add a new one first"); return; }
    if (!form.category) { toast.error("Pick a project category"); return; }
    try {
      const payload = { ...form, budget: Number(form.budget) || 0 };
      if (!isFounder) delete payload.celebration_media_url;
      if (editing) {
        await api.patch(`/projects/${editing.id}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/projects", payload);
        toast.success("Project created (5 default milestones added)");
      }
      setOpen(false);
      setForm(emptyForm());
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteProject = async (p) => {
    if (!window.confirm(`Delete project "${p.name}"? This removes its milestones and chat room.`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Project deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const generateDescription = async () => {
    if (!form.name && !form.client_name) {
      toast.error("Add a project name or client first");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/project-description", {
        name: form.name, client_name: form.client_name, business_unit: form.business_unit,
        seed: form.description,
      });
      setForm((f) => ({ ...f, description: data.description }));
      toast.success("AI summary generated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiLoading(false); }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cover_image: data.url }));
      toast.success("Cover uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const filtered = useMemo(() => {
    const statusFilter = params.get("status"); // active | completed | on_hold | (null = all)
    let list = projects;
    if (filter !== "ALL") list = list.filter((p) => p.business_unit === filter);
    if (statusFilter) list = list.filter((p) => p.status === statusFilter);
    return list;
  }, [projects, filter, params]);

  const clientUsers = clients;

  // Phase 84.62 — Project Insights lives as a view toggle on this same page
  // (not a separate route), per founder's choice.
  const [view, setView] = useState("grid");

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto fade-in" data-testid="projects-page">
      <PageHeader
        eyebrow="Pipeline"
        title="Projects"
        subtitle="Every active engagement across BU1, BU2, BU3 and Operations. Click any tile to dive into milestones and team."
        actions={canCreate && (
          <Button onClick={startCreate} data-testid="new-project-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
            <Plus size={14} className="mr-1.5" /> New project
          </Button>
        )}
      />

      <UnassignedClientRequests projects={projects} />

      <div className="inline-flex rounded-sm border border-slate-200 overflow-hidden mb-4" data-testid="projects-view-toggle">
        <button
          onClick={() => setView("grid")}
          className={`px-3 py-1.5 text-xs inline-flex items-center gap-1.5 ${view === "grid" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          data-testid="projects-view-grid-btn"
        >
          <LayoutGrid size={12} /> Grid
        </button>
        <button
          onClick={() => setView("insights")}
          className={`px-3 py-1.5 text-xs inline-flex items-center gap-1.5 ${view === "insights" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          data-testid="projects-view-insights-btn"
        >
          <BarChart3 size={12} /> Insights
        </button>
        <button
          onClick={() => setView("archive")}
          className={`px-3 py-1.5 text-xs inline-flex items-center gap-1.5 ${view === "archive" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          data-testid="projects-view-registry-btn"
        >
          <ClipboardList size={12} /> Task Registry
        </button>
      </div>

      {view === "insights" ? (
        <ProjectInsights />
      ) : view === "archive" ? (
        <TaskRegistryTab />
      ) : (
      <>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {bulk.canBulk && (
          <div
            className="inline-flex items-center gap-1.5 text-xs text-slate-600 px-2 py-1 border border-slate-200 rounded-sm bg-white cursor-pointer"
            data-testid="projects-select-all-label"
            onClick={() => bulk.toggleAll()}
          >
            <bulk.HeaderCheckbox /> <span>Select all</span>
          </div>
        )}
        {["ALL", ...BU_OPTIONS].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${
              filter === f
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}
          >
            {f === "ALL" ? "All" : f}
          </button>
        ))}
      </div>
      {/* Status chips — driven by ?status= URL param so dashboard tiles deep-link cleanly */}
      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="status-chips">
        {[
          { val: null, label: "Any status" },
          { val: "active", label: "Active" },
          { val: "completed", label: "Completed" },
          { val: "on_hold", label: "On hold" },
        ].map(({ val, label }) => {
          const active = (params.get("status") || null) === val;
          return (
            <button
              key={label}
              onClick={() => {
                const np = new URLSearchParams(params);
                if (val) np.set("status", val); else np.delete("status");
                setParams(np, { replace: true });
              }}
              data-testid={`status-chip-${val || "any"}`}
              className={`px-2.5 py-1 text-[11px] rounded-sm border transition-colors ${
                active ? "bg-[#F54800] text-white border-[#F54800]" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="projects-grid">
        {filtered.slice(0, visible).map((p) => (
          <div key={p.id} className={`card-flat overflow-hidden hover:shadow-sm group transition-shadow relative ${bulk.isSelected(p.id) ? "ring-2 ring-[#F54800]" : ""}`} data-testid={`project-tile-${p.id}`}>
            {bulk.canBulk && (
              <div
                className="absolute top-2 left-2 z-10 bg-white/95 backdrop-blur rounded-sm p-1 shadow-sm"
                onClick={(e) => e.stopPropagation()}
              >
                <bulk.RowCheckbox id={p.id} />
              </div>
            )}
            <Link to={`/projects/${p.id}`} className="block">
              <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                {p.cover_image && <img src={p.cover_image} alt={p.name} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Tag tone="slate">{p.business_unit}</Tag>
                  <Tag tone={projectStatusTone(p.status)}>{(p.status || "").replace(/_/g, " ")}</Tag>
                  {p.category && <Tag tone="orange" data-testid={`project-category-tag-${p.id}`}>{PROJECT_CATEGORY_LABEL[p.category] || p.category}</Tag>}
                </div>
                <div className="font-display text-lg font-bold text-slate-900">{p.name}</div>
                <div className="text-xs text-slate-500 mt-1" data-testid={`project-client-label-${p.id}`}>{p.client_name}</div>
                {p.description && <div className="text-sm text-slate-600 mt-2 line-clamp-2">{p.description}</div>}
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Progress</span>
                    <span className="text-[10px] font-bold text-slate-700" data-testid={`project-progress-pct-${p.id}`}>
                      {p.progress_pct ?? 0}% · {p.milestones_done ?? 0}/{p.milestones_total ?? 0} milestones
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-sm overflow-hidden mb-1.5" data-testid={`project-progress-bar-${p.id}`}>
                    <div className="h-full bg-[#F54800] transition-all" style={{ width: `${p.progress_pct ?? 0}%` }} />
                  </div>
                  {/* Feb 2026 · Show task counts alongside milestones so the
                      numbers reconcile everywhere (previously the header
                      claimed "0/10 milestones done" while 32 tasks were Done,
                      making progress feel wrong). */}
                  {(p.tasks_total ?? 0) > 0 && (
                    <div className="text-[10px] text-slate-500 mb-2" data-testid={`project-tasks-count-${p.id}`}>
                      {p.tasks_done ?? 0}/{p.tasks_total} tasks completed
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Timeline</span>
                    {(() => {
                      const c = projectCountdown(p, milestonesByProject[p.id]);
                      return c ? <span className={`text-[10px] font-semibold ${c.tone}`} data-testid={`project-countdown-${p.id}`}>{c.text}</span> : null;
                    })()}
                  </div>
                  <GanttChart project={p} milestones={milestonesByProject[p.id] || []} compact />
                </div>
              </div>
            </Link>
            {(canEditProj || canDeleteProj) && (
              <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {canEditProj && <button onClick={(e) => { e.preventDefault(); startEdit(p); }} className="p-1.5 bg-white/95 border border-slate-200 rounded-sm hover:bg-white shadow-sm" data-testid={`edit-project-${p.id}`} title="Edit"><Pencil size={12} /></button>}
                {canDeleteProj && <button onClick={(e) => { e.preventDefault(); deleteProject(p); }} className="p-1.5 bg-white/95 border border-slate-200 rounded-sm hover:bg-red-50 text-red-600 shadow-sm" data-testid={`delete-project-${p.id}`} title="Delete"><Trash2 size={12} /></button>}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && <div className="text-sm text-slate-500">No projects yet.</div>}
      </div>

      {filtered.length > visible && (
        <div className="mt-6 flex flex-col items-center gap-2" data-testid="projects-pagination">
          <div className="text-xs text-slate-500" data-testid="projects-pagination-count">
            Showing {visible} of {filtered.length} projects
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setVisible((n) => n + PAGE)}
              data-testid="projects-load-more"
              className="rounded-sm"
            >
              Load {Math.min(PAGE, filtered.length - visible)} more
            </Button>
            <Button
              variant="outline"
              onClick={() => setVisible(filtered.length)}
              data-testid="projects-show-all"
              className="rounded-sm"
            >
              Show all ({filtered.length})
            </Button>
          </div>
        </div>
      )}
      </>
      )}

      <bulk.ActionBar />
      <bulk.ConfirmDialog />

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit project" : "Create new project"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <Label className="label-overline">Project name</Label>
              <Input data-testid="project-name-input" className="rounded-sm mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="label-overline">Business unit</Label>
                <Select value={form.business_unit} onValueChange={(v) => setForm({ ...form, business_unit: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="project-bu-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BU_OPTIONS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-overline">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="project-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-overline">Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="project-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROJECT_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-overline">Service we&apos;re helping with</Label>
                <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="project-service-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.service === "other" && (
                <div>
                  <Label className="label-overline">Specify</Label>
                  <Input className="rounded-sm mt-1" value={form.service_other} onChange={(e) => setForm({ ...form, service_other: e.target.value })} placeholder="e.g. Service deep dive" data-testid="project-service-other" />
                </div>
              )}
            </div>
            {/* Project template — choose Standard or Task-driven before filling more fields */}
            <div>
              <Label className="label-overline">Project template</Label>
              <div className="grid grid-cols-2 gap-2 mt-1" data-testid="project-template-picker">
                {[
                  { v: "standard", label: "Standard", hint: "Hero + milestones + scope (default)" },
                  { v: "task_driven", label: "Task-driven", hint: "Daily tasks board + external collaborators" },
                ].map((t) => {
                  const active = form.template === t.v;
                  return (
                    <button
                      key={t.v}
                      type="button"
                      onClick={() => setForm({ ...form, template: t.v })}
                      className={`text-left p-3 rounded-sm border-2 transition-colors ${active ? "border-[#F54800] bg-[#F54800]/5" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid={`template-${t.v}`}
                    >
                      <div className={`text-xs font-bold ${active ? "text-[#F54800]" : "text-slate-900"}`}>{t.label}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{t.hint}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="label-overline">Client *</Label>
              <Select
                value={form.client_user_id || ""}
                onValueChange={(v) => {
                  const c = clientUsers.find((u) => u.id === v);
                  setForm({ ...form, client_user_id: v, client_name: c ? (c.company || c.name) : form.client_name });
                }}
              >
                <SelectTrigger className="rounded-sm mt-1" data-testid="project-client-user-select"><SelectValue placeholder="Pick a client from Registry" /></SelectTrigger>
                <SelectContent>
                  {clientUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.company || u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400 mt-1">
                Client not listed? Add them in <a href="/admin/registry" className="text-[#F54800] hover:underline" data-testid="project-client-registry-link">Registry</a> first, then come back and select.
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="label-overline">Description</Label>
                <Button type="button" size="sm" variant="outline" onClick={generateDescription} disabled={aiLoading} className="h-7 rounded-sm text-xs" data-testid="ai-generate-description">
                  {aiLoading ? <Loader2 size={12} className="animate-spin mr-1" /> : <Sparkles size={12} className="mr-1 text-[#F54800]" />}
                  AI generate
                </Button>
              </div>
              <Textarea className="rounded-sm mt-1" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label className="label-overline">Cover image</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input className="rounded-sm flex-1" placeholder="Image URL or upload" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} data-testid="project-cover-url" />
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadCover} />
                <Button type="button" variant="outline" className="rounded-sm h-9" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-cover-btn">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                </Button>
              </div>
              {form.cover_image && <img src={form.cover_image} alt="cover preview" className="w-full max-h-32 object-cover rounded-sm mt-2 border border-slate-200" />}
            </div>
            <ScopeListEditor label="Goals" placeholder="e.g. Cut user friction by 40%" items={form.goals} onChange={(v) => setForm({ ...form, goals: v })} testId="goals" />
            <ScopeListEditor label="3 main problems to solve" placeholder="e.g. Onboarding takes 12 minutes" items={form.problems} onChange={(v) => setForm({ ...form, problems: v })} testId="problems" max={3} />
            <ScopeListEditor label="Expected outcomes" placeholder="e.g. Onboarding under 3 min" items={form.outcomes} onChange={(v) => setForm({ ...form, outcomes: v })} testId="outcomes" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label className="label-overline">Start</Label><Input type="date" className="rounded-sm mt-1" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label className="label-overline">End</Label><Input type="date" className="rounded-sm mt-1" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              <div><Label className="label-overline">Budget</Label><Input type="number" className="rounded-sm mt-1" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} /></div>
            </div>
            {!editing && (
              <p className="text-[11px] text-slate-500 italic">5 default milestones (Discovery → DFM) will be added automatically. You can edit them after creation.</p>
            )}
            {/* Phase 77 — admin-only toggle: enable daily-task sync for this project */}
            {canEditProj && (
              <label className="flex items-start gap-2 text-xs text-slate-700 mt-2" data-testid="project-form-daily-task-sync">
                <input
                  type="checkbox"
                  checked={!!form.daily_task_sync}
                  onChange={(e) => setForm({ ...form, daily_task_sync: e.target.checked })}
                  className="mt-0.5 accent-[#F54800]"
                  data-testid="project-form-daily-task-sync-input"
                />
                <span>
                  <span className="font-medium">Enable daily-task sync</span>
                  <span className="block text-[11px] text-slate-500">Lets the team attach quick daily tasks + log time from the dashboard widget to this project.</span>
                </span>
              </label>
            )}
            {/* Feb 2026 — "Cash Fix": which bank account this project's
                revenue lands in by default, used by the backfill + any
                future auto-assignment of source_id on new invoices. */}
            {canEditProj && (
              <div data-testid="project-form-default-source">
                <Label className="label-overline">Default Bank Account</Label>
                <Select
                  value={form.default_source_id || "none"}
                  onValueChange={(v) => setForm({ ...form, default_source_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger className="rounded-sm mt-1" data-testid="project-form-default-source-trigger">
                    <SelectValue placeholder="Not set" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    {financeSources.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">Where this project's revenue is deposited. Used to auto-tag incoming invoices for accurate bank balances.</p>
              </div>
            )}
            {/* Phase 154 — Founder-only, editing an existing project (an
                achievement/completion needs a project to exist first). */}
            {isFounder && editing && (
              <div>
                <Label className="label-overline">Celebration media link</Label>
                <Input
                  value={form.celebration_media_url}
                  onChange={(e) => setForm({ ...form, celebration_media_url: e.target.value })}
                  className="rounded-sm mt-1"
                  placeholder="https://drive.google.com/file/d/…/view"
                  data-testid="project-form-celebration-media"
                />
                <p className="text-[11px] text-slate-500 mt-1">Founder-only. Paste a Google Drive slideshow/GIF/video share link — shown on this project's "Shipped" hero banner instead of the default GIF.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={save} data-testid="create-project-submit" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 21 — multi-step wizard for project creation */}
      <ProjectWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onCreated={() => load()}
      />
    </div>
  );
}
